/**
 * Журнал взаимодействий. Доска — дополнительный вид, не основной.
 * 24.10.2026 — не удалять по требованию владельца системы.
 * CROSS-REF: features/interactions/components/InteractionsTable.tsx, shell/InteractionsShell.tsx
 * Дата: 17.03.2026 / polish 17.07.2026
 */
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { addDays, endOfDay, startOfDay } from 'date-fns';
import { EmptyState, LoadingState, Pagination } from '@/components/ui';
import { useTenantScope } from '@/hooks/useTenantScope';
import { CheckCircle2, Frown, Meh, RotateCcw, Smile, XCircle } from 'lucide-react';
import type { InteractionEventType } from '@/types/interaction';
import { InteractionsKanban } from '@/features/interactions/components/InteractionsKanban';
import { InteractionsTable } from '@/features/interactions/components/InteractionsTable';
import { useInteractionEvents } from '@/features/interactions/hooks/useInteractionEvents';
import { useInteractionFilters } from '@/features/interactions/hooks/useInteractionFilters';
import { useInteractionSummary } from '@/features/interactions/hooks/useInteractionSummary';
import { getInteractionSectionConfig } from '@/features/interactions/registry/sectionRegistry';
import { getInteractionStatusMeta } from '@/features/interactions/utils/interactionDisplay';
import { InteractionsFiltersBar } from '@/features/interactions/shell/InteractionsFiltersBar';
import { InteractionsShell } from '@/features/interactions/shell/InteractionsShell';

const CONFIRMATION_COLUMNS: Array<{
  type: InteractionEventType;
  label: string;
  icon: JSX.Element;
  accentClass: string;
}> = [
  {
    type: 'appointment_confirm',
    label: 'Подтверждено',
    icon: <CheckCircle2 className="h-4 w-4 text-shell-success" />,
    accentClass: 'border-l-shell-success',
  },
  {
    type: 'appointment_cancel',
    label: 'Отменено',
    icon: <XCircle className="h-4 w-4 text-shell-danger" />,
    accentClass: 'border-l-shell-danger',
  },
  {
    type: 'appointment_reschedule',
    label: 'Перенос',
    icon: <RotateCcw className="h-4 w-4 text-shell-warning" />,
    accentClass: 'border-l-shell-warning',
  },
];

const FEEDBACK_COLUMNS: Array<{
  type: InteractionEventType;
  label: string;
  icon: JSX.Element;
  accentClass: string;
}> = [
  {
    type: 'feedback_positive',
    label: 'Понравилось',
    icon: <Smile className="h-4 w-4 text-shell-success" />,
    accentClass: 'border-l-shell-success',
  },
  {
    type: 'feedback_neutral',
    label: 'Нормально',
    icon: <Meh className="h-4 w-4 text-shell-muted" />,
    accentClass: 'border-l-shell-border',
  },
  {
    type: 'feedback_negative',
    label: 'Не понравилось',
    icon: <Frown className="h-4 w-4 text-shell-danger" />,
    accentClass: 'border-l-shell-danger',
  },
];

export function InteractionsDashboardPage() {
  const { tenantId, isSuperAdmin, selectedTenantId, setSelectedTenant } = useTenantScope();
  const filters = useInteractionFilters();
  const activeSection = getInteractionSectionConfig(filters.activeSectionId);

  const eventsQuery = useInteractionEvents({
    tenantId,
    sectionId: filters.activeSectionId,
    search: filters.search,
    page: filters.page,
    dateFrom: filters.dateRange.from,
    dateTo: filters.dateRange.to,
  });
  const summaryQuery = useInteractionSummary(tenantId, filters.dateRange.from, filters.dateRange.to);

  const datePresets = useMemo(
    () => [
      { label: 'Сегодня', from: startOfDay(new Date()), to: endOfDay(new Date()) },
      { label: 'Вчера', from: startOfDay(addDays(new Date(), -1)), to: endOfDay(addDays(new Date(), -1)) },
      { label: '7 дней', from: startOfDay(addDays(new Date(), -6)), to: endOfDay(new Date()) },
      { label: '14 дней', from: startOfDay(addDays(new Date(), -13)), to: endOfDay(new Date()) },
    ],
    [],
  );

  const [view, setView] = useState<'registry' | 'board'>('registry');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const boardColumns = filters.activeSectionId === 'feedback' ? FEEDBACK_COLUMNS : CONFIRMATION_COLUMNS;
  const selectedEvent = eventsQuery.events.find((event) => event.id === selectedId) || null;
  const showBoard = view === 'board' && activeSection.mode === 'board';

  return (
    <InteractionsShell
      filtersBar={
        <InteractionsFiltersBar
          isSuperAdmin={isSuperAdmin}
          selectedTenantId={selectedTenantId || ''}
          onTenantChange={setSelectedTenant}
          activeSectionId={filters.activeSectionId}
          onSectionChange={filters.setActiveSectionId}
          search={filters.search}
          onSearchChange={filters.setSearch}
          dateRange={filters.dateRange}
          onDateRangeChange={(from, to) => filters.setDateRange({ from, to })}
          datePresets={datePresets}
          total={eventsQuery.total}
        />
      }
    >
      <div className="flex h-full min-h-0">
        <div className="min-h-0 min-w-0 flex-1 overflow-auto p-3">
          <div className="mb-2 flex flex-wrap items-center gap-2 text-sm">
            <span className="text-shell-muted">За период:</span>
            <span className="rounded-lg bg-shell-success-soft px-2 py-1 text-shell-success">Подтверждено {summaryQuery.summaryMap.appointment_confirm || 0}</span>
            <span className="rounded-lg bg-shell-danger-soft px-2 py-1 text-shell-danger">Отменено {summaryQuery.summaryMap.appointment_cancel || 0}</span>
            <span className="rounded-lg bg-shell-warning-soft px-2 py-1 text-shell-warning">Перенос {summaryQuery.summaryMap.appointment_reschedule || 0}</span>
            {activeSection.mode === 'board' && (
              <button type="button" className="shell-button-secondary h-8" onClick={() => setView((current) => current === 'board' ? 'registry' : 'board')}>
                {showBoard ? 'Журнал' : 'Доска'}
              </button>
            )}
          </div>
          {eventsQuery.isLoading ? (
            <LoadingState text="Загрузка событий..." />
          ) : eventsQuery.events.length === 0 ? (
            <EmptyState title={`Нет данных: ${activeSection.label}`} description={activeSection.emptyDescription} />
          ) : showBoard ? (
            <InteractionsKanban columnsConfig={boardColumns} columns={eventsQuery.kanbanColumns} summaryMap={summaryQuery.summaryMap} />
          ) : (
            <>
              <InteractionsTable events={eventsQuery.events} selectedId={selectedId} onSelect={setSelectedId} />
              {eventsQuery.total > eventsQuery.pageSize && (
                <Pagination
                  offset={filters.page * eventsQuery.pageSize}
                  limit={eventsQuery.pageSize}
                  total={eventsQuery.total}
                  onOffsetChange={(newOffset) => filters.setPage(Math.floor(newOffset / eventsQuery.pageSize))}
                />
              )}
            </>
          )}
        </div>
        {selectedEvent && !showBoard && (
          <aside className="w-[340px] shrink-0 overflow-y-auto border-l border-shell-border bg-shell-surface p-4">
            <div className="mb-3 flex items-center justify-between">
              <p className="text-sm font-semibold text-shell-strong">Событие</p>
              <button type="button" className="text-xs text-shell-muted" onClick={() => setSelectedId(null)}>Закрыть</button>
            </div>
            <p className="text-sm font-semibold text-shell-text">{selectedEvent.patient_name || 'Пациент'}</p>
            <p className="text-sm text-shell-muted">{selectedEvent.phone}</p>
            <p className="mt-3 text-sm text-shell-text">{selectedEvent.button_label || selectedEvent.event_type}</p>
            <p className="mt-1 text-xs text-shell-muted">Канал: {selectedEvent.source_channel}</p>
            <p className="text-xs text-shell-muted">Статус: {getInteractionStatusMeta(selectedEvent.status).label}</p>
            {selectedEvent.message_id && <p className="mt-2 break-all text-xs text-shell-muted">message_id: {selectedEvent.message_id}</p>}
            <Link className="mt-4 inline-flex text-sm font-semibold text-shell-primary-strong" to={`/messages?phone=${encodeURIComponent(selectedEvent.phone_normalized || selectedEvent.phone)}`}>
              Открыть переписку
            </Link>
          </aside>
        )}
      </div>
    </InteractionsShell>
  );
}
